/**
 * A stable identity for one `@querystring-search` request.
 *
 * WHY THIS EXISTS (2026-09-27)
 *
 * Every browse page SSRs its first page of results (Browse/
 * getSearchBlockAsyncData), and then `withQuerystringResults` re-sent the
 * identical request from its mount effect the moment the page hydrated. For a
 * visitor that was one duplicate Solr POST per page view (12,736 POSTs to
 * `@querystring-search` from non-search-engine clients in 14 days [edge logs
 * 2026-09-13..27]). For a rendering crawler it was worse: robots.txt had
 * Disallowed `/++api++/` since 2026-09-20, so Googlebot and Bingbot never made
 * that request, the action failed, core's reducer wrote `total: 0`, and
 * BrowseSEO turned every hub and `?preset=` page into `noindex,follow` in the
 * rendered DOM — while curl, which does not run the script, saw
 * `index,follow` and "1,802 documents". The page told the crawler the
 * opposite of what the server said.
 *
 * Both sides now stamp the action with this key. The reducer stores it on
 * success; the mount effect skips its request when the subrequest already
 * holds a successful result for the same key. A key that differs for any
 * reason simply means the request is sent, which is the old behaviour, so a
 * mismatch can only cost a duplicate request, never a wrong result.
 *
 * The key is built from the action's own `request` (what would actually be
 * POSTed, after core's normalisation of depth, sort_order and b_start), not
 * from the caller's input, so the server seed and the client cannot disagree
 * about the shape of the same query.
 *
 * It keeps only the parameters the client can send. The server seed spreads
 * the block's whole `query` object into the request; a key the client never
 * forwards (see `copyFields` in withQuerystringResults) is not part of what
 * the client would ask for, so it must not make the two look different.
 *
 * Criteria are compared as a SET: `withFacets` on the server and `withSearch`
 * on the client can append the same rows in a different order, and the order
 * of AND-ed criteria does not change the result.
 */
const REQUEST_FIELDS = [
  'b_size',
  'b_start',
  'correct',
  'depth',
  'fullobjects',
  'limit',
  'metadata_fields',
  'mode',
  'operator',
  'query',
  'sort_on',
  'sort_order',
  'status_group',
];

/** JSON with object keys sorted at every depth; array order is kept. */
const stable = (value) => {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((out, key) => {
        out[key] = stable(value[key]);
        return out;
      }, {});
  }
  return value;
};

export const querystringKey = (action) => {
  const request = action?.request;
  if (!request?.path) return null;
  const data = request.data || {};
  const picked = {};
  REQUEST_FIELDS.forEach((name) => {
    if (data[name] !== undefined && data[name] !== null) {
      picked[name] = stable(data[name]);
    }
  });
  if (Array.isArray(picked.query)) {
    picked.query = picked.query
      .map((row) => JSON.stringify(row))
      .sort()
      .map((row) => JSON.parse(row));
  }
  // `path` also carries the GET-mode query string when
  // settings.querystringSearchGet is on, so it identifies that mode too.
  return JSON.stringify([request.op, request.path, picked]);
};

/** The same action, stamped with its key for the reducer to keep. */
export const withQuerystringKey = (action) => ({
  ...action,
  queryKey: querystringKey(action),
});
